import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import { useHGS } from '../context/HGSContext';
import { TASK_STATUS, TASK_STATUS_META, TASK_STATUS_ORDER, escalationOf, tasksForUser } from '../data/taskEngine';
import {
  ClipboardCheck, ShieldCheck, ClipboardList, FileSignature, AlertTriangle, Inbox,
  ChevronRight, Clock, ArrowUpRight, Filter, Users, CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MODULE_META = {
  risk:  { label: 'Risk Evaluation', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: ClipboardCheck, route: '/risk' },
  fraud: { label: 'Fraud',           tone: 'bg-sky-50 text-sky-700 border-sky-200',             icon: ShieldCheck,    route: '/fraud' },
  pcc:   { label: 'PCC',             tone: 'bg-violet-50 text-violet-700 border-violet-200',    icon: ClipboardList,  route: '/pcc' },
  dlc:   { label: 'DLC',             tone: 'bg-amber-50 text-amber-700 border-amber-200',       icon: FileSignature,  route: '/dlc' },
};

const ESCALATION_TONE = {
  L1: { tone: 'bg-amber-50 border-amber-200 text-amber-900', badge: 'bg-amber-500' },
  L2: { tone: 'bg-red-50 border-red-200 text-red-900',       badge: 'bg-red-500' },
  L3: { tone: 'bg-rose-50 border-rose-300 text-rose-900',    badge: 'bg-rose-600' },
};

export default function TasksPage() {
  const { user, tasks } = useHGS();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleFilter = searchParams.get('module') || '';
  const statusFilter = searchParams.get('status') || '';
  const onlyOverdue = searchParams.get('overdue') === '1';

  // Scope by role (Admin sees all; OH → their domain; DivH → their division; DH → their dept)
  const scopedTasks = useMemo(() => tasksForUser(tasks || [], user), [tasks, user]);

  const decorated = useMemo(() => scopedTasks.map((t) => ({ ...t, escalation: escalationOf(t) })), [scopedTasks]);

  const filtered = useMemo(() => {
    return decorated.filter((t) => {
      if (moduleFilter && t.module !== moduleFilter) return false;
      if (statusFilter && t.status !== statusFilter) return false;
      if (onlyOverdue && !t.escalation) return false;
      return true;
    });
  }, [decorated, moduleFilter, statusFilter, onlyOverdue]);

  const kpi = useMemo(() => {
    let total = decorated.length;
    let draft = 0, submitted = 0, underReview = 0, approved = 0, finalApproved = 0, rejected = 0, overdue = 0;
    decorated.forEach((t) => {
      if (t.status === TASK_STATUS.DRAFT) draft += 1;
      else if (t.status === TASK_STATUS.SUBMITTED) submitted += 1;
      else if (t.status === TASK_STATUS.UNDER_REVIEW) underReview += 1;
      else if (t.status === TASK_STATUS.APPROVED) approved += 1;
      else if (t.status === TASK_STATUS.FINAL_APPROVED) finalApproved += 1;
      else if (t.status === TASK_STATUS.REJECTED) rejected += 1;
      if (t.escalation) overdue += 1;
    });
    return { total, draft, submitted, underReview, approved, finalApproved, rejected, overdue };
  }, [decorated]);

  const setQ = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || next.get(key) === value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => setSearchParams({}, { replace: true });

  const humanize = (slug) => (slug || '')
    .split('-')
    .map((w) => w ? w[0].toUpperCase() + w.slice(1) : '')
    .join(' ');

  return (
    <div>
      <TopBar
        title="Task Engine"
        subtitle="Auto-generated from upstream events · risk applicability publish → OH evaluation → DH module fills"
        breadcrumbs={['HGS', 'Tasks']}
      />
      <div className="p-8 space-y-5">

        {/* Identity / scope banner */}
        <div className="flex items-start gap-4 p-5 rounded-xl bg-gradient-to-r from-slate-900 to-[#0a1830] text-white" data-testid="tasks-scope-banner">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center"><Inbox className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">
              TASK ENGINE · {user?.role ? user.role.replace(/_/g, ' ').toUpperCase() : 'ALL'} SCOPE
            </div>
            <div className="text-base font-bold">
              {user?.role === 'admin' ? 'Enterprise-wide task ledger' :
               user?.role === 'operating_head' ? `${humanize(user?.operatingHead)} domain` :
               user?.role === 'division_head' ? `${humanize(user?.division)} Division` :
               user?.role === 'department_head' ? `${humanize(user?.department)} Desk` :
               'Your assigned work queue'}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              {kpi.total} task{kpi.total === 1 ? '' : 's'} · {kpi.overdue} overdue · Draft → Submitted → Under Review → Approved → Final Approved
            </div>
          </div>
          <button
            data-testid="tasks-clear-filters"
            onClick={clearAll}
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-[11px] font-bold inline-flex items-center gap-1.5"
          >
            <Filter className="w-3 h-3" /> Reset filters
          </button>
        </div>

        {/* Status pipeline KPIs (Draft → Submitted → UnderReview → Approved → FinalApproved) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="tasks-status-pipeline">
          <StatusTile label="Draft"          value={kpi.draft}         active={statusFilter === TASK_STATUS.DRAFT}          tone="slate"   onClick={() => setQ('status', TASK_STATUS.DRAFT)}        testId="status-draft" />
          <StatusTile label="Submitted"      value={kpi.submitted}     active={statusFilter === TASK_STATUS.SUBMITTED}      tone="blue"    onClick={() => setQ('status', TASK_STATUS.SUBMITTED)}    testId="status-submitted" />
          <StatusTile label="Under Review"   value={kpi.underReview}   active={statusFilter === TASK_STATUS.UNDER_REVIEW}   tone="amber"   onClick={() => setQ('status', TASK_STATUS.UNDER_REVIEW)} testId="status-under-review" />
          <StatusTile label="Approved"       value={kpi.approved}      active={statusFilter === TASK_STATUS.APPROVED}       tone="emerald" onClick={() => setQ('status', TASK_STATUS.APPROVED)}    testId="status-approved" />
          <StatusTile label="Final Approved" value={kpi.finalApproved} active={statusFilter === TASK_STATUS.FINAL_APPROVED} tone="green"   onClick={() => setQ('status', TASK_STATUS.FINAL_APPROVED)} testId="status-final" />
          <StatusTile label="Overdue"        value={kpi.overdue}       active={onlyOverdue}                                  tone="red"     onClick={() => setQ('overdue', onlyOverdue ? '' : '1')} testId="status-overdue" icon={Clock} accent />
        </div>

        {/* Module chips */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Module:</span>
          {[
            { k: '', l: 'All' },
            { k: 'risk', l: 'Risk Evaluation' },
            { k: 'fraud', l: 'Fraud' },
            { k: 'pcc', l: 'PCC' },
            { k: 'dlc', l: 'DLC' },
          ].map((f) => (
            <button
              key={f.k || 'all'}
              data-testid={`task-module-${f.k || 'all'}`}
              onClick={() => setQ('module', f.k)}
              className={cn(
                'px-2.5 py-1 rounded-md text-[10px] font-bold transition',
                (moduleFilter || '') === f.k ? 'bg-[#CC0000] text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100',
              )}
            >
              {f.l}
            </button>
          ))}
          <span className="ml-auto text-[11px] text-slate-500">Showing {filtered.length} of {decorated.length}</span>
        </div>

        {/* Escalation banner (when any overdue) */}
        {kpi.overdue > 0 && !onlyOverdue && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3" data-testid="overdue-banner">
            <div className="w-9 h-9 rounded-lg bg-red-500 text-white flex items-center justify-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-red-900">{kpi.overdue} task{kpi.overdue === 1 ? '' : 's'} overdue</h3>
              <p className="text-[11px] text-red-700">Escalations are auto-tiered: L1 (≤7 days) Division Head · L2 (≤21 days) Operating Head · L3 (&gt;21 days) GRC Secretariat.</p>
            </div>
            <button
              data-testid="show-overdue-only"
              onClick={() => setQ('overdue', '1')}
              className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold inline-flex items-center gap-1.5"
            >
              View overdue
              <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Task list */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="tasks-list">
          {filtered.length === 0 ? (
            <div className="p-12 text-center" data-testid="tasks-list-empty">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-3" />
              <p className="text-sm font-bold text-slate-700">No tasks match the current scope &amp; filters</p>
              <p className="text-xs text-slate-500 mt-1">
                {!user || tasks.length === 0
                  ? 'Tasks are auto-generated after the GRC Secretariat publishes risk applicability and Operating Heads confirm their evaluations.'
                  : 'Try clearing module or status filters.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[800px] overflow-y-auto">
              {filtered.map((t) => (
                <TaskRow key={t.taskId} task={t} onOpen={() => navigate(MODULE_META[t.module]?.route || '/')} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskRow({ task, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const M = MODULE_META[task.module] || MODULE_META.risk;
  const statusMeta = TASK_STATUS_META[task.status] || TASK_STATUS_META.Draft;
  const esc = task.escalation;
  const escMeta = esc ? ESCALATION_TONE[esc.level] : null;

  const due = new Date(task.dueDate);
  const dueLabel = due.toLocaleDateString();

  return (
    <div
      className={cn('p-4 flex items-start gap-3 transition hover:bg-slate-50', esc && 'bg-red-50/30')}
      data-testid={`task-row-${task.taskId}`}
    >
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border', M.tone)}>
        <M.icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold uppercase', M.tone)}>{M.label}</span>
          <span className="font-mono text-[10px] text-slate-500">{task.taskId}</span>
          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold', statusMeta.tone)} data-testid={`task-status-${task.taskId}`}>
            {statusMeta.label}
          </span>
          {esc && escMeta && (
            <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border', escMeta.tone)} data-testid={`task-escalation-${task.taskId}`}>
              <span className={cn('w-1.5 h-1.5 rounded-full', escMeta.badge)} />
              {esc.level} · {esc.days}d overdue
            </span>
          )}
          {task.linkedRiskId && (
            <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">linked: {task.linkedRiskId}</span>
          )}
        </div>
        <p className="text-sm font-semibold text-slate-900 truncate">
          {task.moduleQuestion || task.riskTitle || `${M.label} task`}
        </p>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1"><Users className="w-2.5 h-2.5" /> {task.assignedUser}</span>
          {task.departmentLabel && <span>· {task.departmentLabel}</span>}
          {task.operatingHeadLabel && <span>· {task.operatingHeadLabel}</span>}
          <span>· Due <span className={cn('font-bold', esc ? 'text-red-700' : 'text-slate-700')}>{dueLabel}</span></span>
        </div>
        {expanded && task.history && task.history.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100 space-y-1" data-testid={`task-history-${task.taskId}`}>
            {task.history.map((h, i) => (
              <p key={`h-${task.taskId}-${i}`} className="text-[10px] text-slate-600">
                <span className="font-mono text-slate-400">{new Date(h.at).toLocaleString()}</span> · {h.action}
              </p>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        <button
          onClick={onOpen}
          data-testid={`task-open-${task.taskId}`}
          className="px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold inline-flex items-center gap-1"
        >
          Open <ChevronRight className="w-3 h-3" />
        </button>
        <button
          onClick={() => setExpanded((p) => !p)}
          data-testid={`task-toggle-history-${task.taskId}`}
          className="px-3 py-1 rounded-md bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-bold"
        >
          {expanded ? 'Hide' : 'History'}
        </button>
      </div>
    </div>
  );
}

function StatusTile({ label, value, tone, onClick, active, testId, icon: Icon, accent }) {
  const toneMap = {
    slate:   'bg-slate-50 text-slate-700 border-slate-200',
    blue:    'bg-blue-50 text-blue-800 border-blue-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    green:   'bg-emerald-100 text-emerald-900 border-emerald-300',
    red:     'bg-red-50 text-red-800 border-red-200',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'text-left rounded-xl border p-3 transition',
        toneMap[tone] || toneMap.slate,
        accent && 'border-l-4 border-l-[#CC0000]',
        'hover:shadow-sm hover:-translate-y-px cursor-pointer',
        active && 'ring-2 ring-[#CC0000] ring-offset-1',
      )}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </button>
  );
}
